import type { FaqItem, VerifyStatus } from "@/types/content";

export const siteInfo = {
  name: "511 求证档案",
  shortName: "求证档案",
  tagline: "细节补充 · 时间线整理 · 来源求实",
  intro:
    "511 求证档案是一个公开的信息梳理入口。我们把关于「511 爆料」的碎片信息重新排列，逐条标注来源、补充细节、对照证据，让时间线清晰可查，让每一条说法都能追到出处。",
  email: "verify@511-archive.example.com",
  community: "求证笔记 · 读者来信",
  updatedAt: "2026 年 5 月 20 日",
};

export const navLinks = [
  { label: "首页", to: "/" },
  { label: "时间线整理", to: "/timeline" },
  { label: "证据对照室", to: "/evidence" },
  { label: "求证笔记", to: "/articles" },
  { label: "常见问题", to: "/faq" },
  { label: "关于我们", to: "/about" },
];

export const overviewStats = [
  { label: "已收录线索", value: "128", unit: "条", hint: "含转载与二次加工内容" },
  { label: "已完成核实", value: "73", unit: "条", hint: "至少两个独立来源可对照" },
  { label: "存在矛盾点", value: "19", unit: "处", hint: "时间、地点或身份对不上" },
  { label: "仍在求证", value: "36", unit: "条", hint: "单一来源，等待补充" },
];

export const statusMeta: Record<
  VerifyStatus,
  { label: string; description: string }
> = {
  verified: {
    label: "已核实",
    description: "存在两个及以上相互独立的来源，且关键细节可以互相对上。",
  },
  pending: {
    label: "待证实",
    description: "目前只有单一来源，或来源无法独立求证，暂作存疑记录。",
  },
  conflict: {
    label: "存在矛盾",
    description: "不同来源在时间、地点或身份描述上互相冲突，尚未排除。",
  },
};

export const sourceLevels = [
  { level: "A 级", label: "一手材料", desc: "原始截图、原始文件、可验证时间戳，未经转述。" },
  { level: "B 级", label: "直接转述", desc: "当事人或直接关联方的公开表述，可定位到原始出处。" },
  { level: "C 级", label: "二手整理", desc: "他人已整理过的内容，需要回到其引用的源头复核。" },
  { level: "D 级", label: "无法溯源", desc: "找不到最早出处，仅能作为线索记录，不作为结论。" },
];

export const faqItems: FaqItem[] = [
  {
    question: "511 爆料档案里的内容，是已经确认的事实吗？",
    answer:
      "不是。我们把内容分成「已核实」「待证实」「存在矛盾」三类。只有同时满足两个以上相互独立、且关键细节可对照的来源，才会标为已核实。其余内容以线索形式保留，会明确标注状态，避免读者误当成结论。",
  },
  {
    question: "你们如何判断两个来源是否「相互独立」？",
    answer:
      "关键看是否来自同一条原始链路。如果两个账号只是互相转发同一张截图，那它们算同一个来源，不算两个。只有当两条信息可以各自追溯到互不相关的起点时，我们才认为它们相互独立。",
  },
  {
    question: "发现自己的说法被标注为「存在矛盾」，可以提出补充吗？",
    answer:
      "可以，而且我们欢迎。请在联系页面提交你掌握的材料与出处，我们会把它加入对照表，重新评估该条目的状态。标注不是定论，而是当前所有可查材料的综合结果。",
  },
  {
    question: "为什么有些细节一直停留在「待证实」？",
    answer:
      "常见原因有三种：只有单一来源、来源已被删除无法回查、或者关键信息涉及隐私不便公开。只要缺少其中任一条件，我们都会保留存疑状态，不用推测去补全。",
  },
  {
    question: "档案里的时间以哪个时区为准？",
    answer:
      "统一使用北京时间（UTC+8）。如果原始材料标注的是其他时区，我们会在条目里注明换算过程，方便你自行核对。",
  },
  {
    question: "你们会主动删除已经录入的内容吗？",
    answer:
      "一般不会。如果发现原始材料错误，我们会在条目上追加更正说明，并保留修改记录，而不是直接抹掉。只有涉及个人隐私、联系方式一类信息时，才会做脱敏或移除处理。",
  },
];

export const principles = [
  {
    title: "先标来源，再谈结论",
    desc: "任何一条说法进入档案前，先确认它最早出现在哪里。找不到源头的，只作为线索登记，不进入结论区。",
  },
  {
    title: "对照优先于判断",
    desc: "我们更愿意呈现两份材料哪里一致、哪里打架，而不是替读者下判断。矛盾本身就是有用的信息。",
  },
  {
    title: "存疑就写存疑",
    desc: "材料不足时如实标注「待证实」，不用推测填空。宁可慢一点，也不让档案里出现没有出处的断言。",
  },
  {
    title: "保留修改痕迹",
    desc: "条目更新时附上时间和原因，读者能看到它被改过什么，而不是面对一个悄无声息变化的页面。",
  },
];